import { Link } from "react-router-dom";
import { Calendar, Phone, Mail, ArrowUpRight } from "lucide-react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { useContent } from "@/hooks/useContent";
import { FormDownload } from "@/components/FormDownload";

const HHD_IMAGE = "/hhd-schedule-2026.png";

export default function HighHolyDays() {
  // Admin-editable rich-text block. When empty, the hardcoded schedule image
  // + contact info render. When populated, it fully replaces the body.
  const { body: adminBody, loading } = useContent("high_holy_days.body", "");
  const hasAdminContent = !loading && !!(adminBody && adminBody.trim() && adminBody !== "<p></p>");

  return (
    <>
      <SEOHead
        title="High Holy Days 5787 - Schedule of Services"
        description="High Holy Days 5787 (2026) schedule of services at Temple Israel of Brevard - S'lichot, Rosh Hashanah, Yom Kippur, Sukkot, and Simchat Torah service times."
        path="/calendar/high-holy-days"
      />
      <PageHero
        eyebrow="High Holy Days · 5787"
        title="Schedule of Services"
        subtitle="Join us for the sacred days of awe - from S'lichot through Simchat Torah."
      />

      <section className="bg-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-10">
          {hasAdminContent ? (
            <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: adminBody }} />
          ) : (
            <>
              {/* Schedule image - tap for a larger view */}
              <a
                href={HHD_IMAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-2xl overflow-hidden border border-[var(--line)] shadow-lg hover:shadow-2xl transition-shadow"
                data-testid="hhd-schedule-image-link"
              >
                <img
                  src={HHD_IMAGE}
                  alt="High Holy Days 5787 Schedule of Services at Temple Israel of Brevard"
                  className="w-full h-auto block"
                  loading="eager"
                  data-testid="hhd-schedule-image"
                />
              </a>
              <p className="mt-3 text-xs text-[var(--muted)] text-center">
                Tap the schedule to view a larger, printable version.
              </p>

              <div className="mt-10 rounded-2xl bg-[var(--cream)] p-6 md:p-7 border border-[var(--line)]">
                <h2 className="font-display text-2xl text-[var(--blue-ink)] mb-3">
                  Tickets &amp; Membership
                </h2>
                <p className="text-[var(--ink)] leading-relaxed">
                  Rosh Hashanah and Yom Kippur services are open to all members
                  and to non-members who reserve tickets in advance. To purchase
                  High Holy Day tickets or inquire about membership, please
                  contact the temple office.
                </p>
                <div className="mt-5 flex flex-wrap gap-4 text-sm">
                  <a
                    href="tel:3216319494"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[var(--blue-deep)] border border-[var(--line)] hover:bg-[var(--blue)]/5"
                  >
                    <Phone size={14} /> (321) 631-9494
                  </a>
                  <a
                    href="mailto:tioffice@tiofbrevard.com"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[var(--blue-deep)] border border-[var(--line)] hover:bg-[var(--blue)]/5"
                  >
                    <Mail size={14} /> tioffice@tiofbrevard.com
                  </a>
                </div>

                <FormDownload
                  label="Tishrei Ticket Order Form"
                  file="/forms/tishrei-form.pdf"
                  description="Printable order form for High Holy Day tickets. Download, complete, and return to the temple office."
                  className="not-prose mt-6"
                />
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/calendar/month" className="btn-primary" data-testid="hhd-calendar-cta">
                  <Calendar size={16} /> See the full Temple Calendar
                </Link>
                <Link to="/worship/holidays" className="btn-ghost">
                  Holidays throughout the year <ArrowUpRight size={14} />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
